// eslint-disable react-hooks/exhaustive-deps
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Icon, Typography, SegmentedButtons } from '@scale/scaleui';
import SelectScalien from 'react-scale/corp/components/UI/SelectScalien';
import { IUserDetail } from 'react-scale/components/WebinarSessions/types';
import { lookupUserIdsOrEmails } from '../api';
import { UserIdsOrEmailsTextInput } from './UserIdsOrEmailsTextInput';

interface IHostsToolProps {
  hostedBy: string[];
  setHostedBy: (ids: string[]) => void;
  disabled?: boolean;
}

enum HostSearchMode {
  Search = 'Search',
  List = 'List',
}

export const HostsTool: React.FC<IHostsToolProps> = ({ hostedBy, setHostedBy, disabled }) => {
  const [hostSearchMode, setHostSearchMode] = useState<HostSearchMode>(HostSearchMode.Search);
  const [hosts, setHosts] = useState<IUserDetail[]>([]);
  const hostIds = useMemo(() => {
    const ids = hosts.map(host => host.id);
    setHostedBy(ids);
    return ids;
  }, [hosts, setHostedBy]);
  const handleSelectHost = useCallback(
    (user: any) => {
      const userId = user.value;
      if (!hostIds.includes(userId)) setHosts(h => [...h, { id: userId, email: user.label }]);
    },
    [hostIds],
  );
  const removeHost = useCallback(
    (hostId: string) => setHosts(h => h.filter(host => host.id !== hostId)),
    [],
  );

  useEffect(() => {
    if (hostedBy?.length) {
      (async () => {
        const users = await lookupUserIdsOrEmails(hostedBy);
        setHosts(users);
      })();
    }
  }, []);

  return (
    <>
      <div className="flex gap-1 items-center mb-1">
        <Typography.Subtitle3 className="text-neutral-600">
          Host(s) - select +worker account for @scale.com emails (optional)
        </Typography.Subtitle3>
        <Icon
          icon="info"
          size="sm"
          rounded
          variant="neutral"
          hasBackground
          tooltip="Hosts will be able to view, edit, and start this webinar"
          tooltipProps={{ side: 'left' }}
        />
      </div>
      {!disabled && (
        <div className="flex gap-2 mb-1 items-center">
          <SegmentedButtons
            selected={hostSearchMode}
            onSelect={(h: string) => setHostSearchMode(h as HostSearchMode)}
            buttons={Object.values(HostSearchMode).map(m => ({ id: m, label: m }))}
            className="mb-1"
          />
          <div className="w-full">
            {hostSearchMode === HostSearchMode.List && (
              <UserIdsOrEmailsTextInput
                setUsers={setHosts}
                placeholder="Paste in user IDs or emails (use +worker accounts), separated by any delimiter"
              />
            )}
            {hostSearchMode === HostSearchMode.Search && (
              <SelectScalien
                placeholder="Search for a scalien"
                onChange={handleSelectHost}
                filterOption={() => true}
              />
            )}
          </div>
        </div>
      )}
      <div className="flex flex-col max-h-[108px] overflow-y-auto overflow-x-hidden relative">
        {hosts.map((host: IUserDetail) => (
          <div key={host.id} className="flex gap-1 items-center">
            <Typography.Paragraph3>
              • {host.email}
              {host.name ? ` (${host.name})` : ''}
            </Typography.Paragraph3>
            {!disabled && (
              <Icon size="sm" icon={['far', 'trash']} onClick={() => removeHost(host.id)} />
            )}
          </div>
        ))}
      </div>
    </>
  );
};
